import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { buildWhatsAppUrl, sanitizeHtml } from "@/lib/content";
import BrandLogo from "@/components/BrandLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "-9999px";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();

  try {
    const copied = document.execCommand("copy");
    if (!copied) {
      throw new Error("Copy command failed");
    }
  } finally {
    document.body.removeChild(textArea);
  }
};

const shouldUseNativeShare = () => {
  if (!navigator.share) {
    return false;
  }

  return window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center" data-testid="post-logo-link">
            <BrandLogo />
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/blog"
              className="flex items-center gap-2 text-stone-600 hover:text-[#073D2D] transition-colors"
              data-testid="back-to-blog-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">All Posts</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

// Main Blog Post Page Component
export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts/${slug}`);
      setPost(response.data);
    } catch (err) {
      console.error("Error fetching blog post:", err);
      if (err.response?.status === 404) {
        setError("Post not found");
      } else {
        setError("Failed to load post");
      }
      // Use placeholder post
      setPost({
        id: 1,
        title: "Guide to Shopping at Balogun Market",
        content: `
          <h2>Introduction to Balogun Market</h2>
          <p>Balogun Market is one of the largest and most vibrant markets in Lagos, Nigeria. Located on Lagos Island, it's the go-to destination for fabrics, clothing, and fashion accessories. Whether you're a retailer looking for wholesale goods or an individual seeking quality materials, Balogun Market has something for everyone.</p>
          
          <h2>What You Can Find</h2>
          <p>The market is famous for its extensive collection of:</p>
          <ul>
            <li><strong>Fabrics:</strong> Lace, ankara, aso-oke, adire, and more</li>
            <li><strong>Ready-to-wear:</strong> Traditional and modern clothing</li>
            <li><strong>Accessories:</strong> Shoes, bags, jewelry, and headgear</li>
            <li><strong>Household items:</strong> Beddings, curtains, and more</li>
          </ul>
          
          <h2>Tips for Shopping at Balogun</h2>
          <h3>1. Go Early</h3>
          <p>The best time to visit is early morning (around 8-9 AM) when the market is less crowded and vendors are fresh. You might also get better prices as many traders believe the first sale of the day brings good luck.</p>
          
          <h3>2. Know Your Prices</h3>
          <p>Before visiting, research the average prices for items you want. This helps you negotiate better and avoid being overcharged.</p>
          
          <h3>3. Bargain Smartly</h3>
          <p>Bargaining is expected at Balogun Market. Start at about 50% of the quoted price and work your way up. Be respectful but firm in your negotiations.</p>
          
          <h3>4. Inspect Quality</h3>
          <p>Always check the quality of items before purchasing. For fabrics, feel the texture, check for any defects, and ensure the measurements are correct.</p>
          
          <h2>Working with EKO Markets Procurement</h2>
          <p>If you can't visit the market yourself, EKO Markets Procurement can help you source quality items from Lagos markets. Our experienced shoppers know the market inside out and can get you the best deals while ensuring quality.</p>
          
          <p>Contact us today to start your stress-free market sourcing experience!</p>
        `,
        excerpt: "Discover the best tips for navigating Lagos' largest fabric and fashion market.",
        published_date: "2024-12-15",
        featured_image_url:
          "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=1200",
        slug: "guide-balogun-market",
      });
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const handleShare = async () => {
    const url = window.location.href;

    if (shouldUseNativeShare() && navigator.canShare?.({ url }) !== false) {
      try {
        await navigator.share({
          title: post?.title,
          text: post?.excerpt,
          url: url,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }
      }

    }

    try {
      await copyTextToClipboard(url);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Unable to copy link. Please copy it from the address bar.");
    }
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.replace(/<[^>]*>/g, "").split(/\s+/).length || 0;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fff7d6]">
        <Header />
        <div className="pt-24 md:pt-32">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Skeleton className="h-8 w-3/4 mb-4" />
            <Skeleton className="h-4 w-48 mb-8" />
            <Skeleton className="h-96 w-full rounded-xl mb-8" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fff7d6]" data-testid="blog-post-page">
      <Header />

      <article className="pt-24 md:pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Post Header */}
          <motion.header
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mb-8"
          >
            <h1
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] mb-6"
              data-testid="post-title"
            >
              {post?.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-stone-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post?.published_date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{calculateReadTime(post?.content)}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="ml-auto"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </motion.header>

          {/* Featured Image */}
          {post?.featured_image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-10"
            >
              <img
                src={post.featured_image_url}
                alt={post.title}
                className="w-full h-auto rounded-2xl shadow-lg"
                data-testid="post-featured-image"
              />
            </motion.div>
          )}

          {/* Post Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="prose prose-lg prose-stone max-w-none"
            data-testid="post-content"
          >
            <div
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post?.content) }}
              className="
                [&>h2]:font-heading [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-[#1C1917] [&>h2]:mt-10 [&>h2]:mb-4
                [&>h3]:font-heading [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-[#1C1917] [&>h3]:mt-8 [&>h3]:mb-3
                [&>p]:text-stone-700 [&>p]:leading-relaxed [&>p]:mb-4
                [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4 [&>ul>li]:text-stone-700 [&>ul>li]:mb-2
                [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-4 [&>ol>li]:text-stone-700 [&>ol>li]:mb-2
                [&>blockquote]:border-l-4 [&>blockquote]:border-[#073D2D] [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-stone-600
                [&_strong]:font-semibold [&_strong]:text-[#1C1917]
                [&_a]:text-[#073D2D] [&_a]:underline [&_a:hover]:text-[#052f23]
              "
            />
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-12 p-8 bg-[#073D2D] rounded-2xl text-center"
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-3">
              Need help sourcing from Lagos markets?
            </h3>
            <p className="text-white/80 mb-6">
              Let EKO Markets Procurement handle your market sourcing needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button
                  className="bg-[#e7c139] hover:bg-[#c5a32f] text-black rounded-full px-6"
                  data-testid="post-cta-request-btn"
                >
                  Request a Shopper
                </Button>
              </Link>
              <a
                href={buildWhatsAppUrl(
                  `Hello! I just read your blog post about ${post?.title} and I'm interested in your services.`
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#073D2D] rounded-full px-6"
                  data-testid="post-cta-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Back to Blog */}
          <div className="mt-8 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#073D2D] font-medium hover:underline"
              data-testid="back-to-all-posts-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all posts
            </Link>
          </div>
        </div>
      </article>

      {/* WhatsApp Float */}
      <a
        href={buildWhatsAppUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        data-testid="post-whatsapp-float"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
